import * as Y from "yjs";
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates } from "y-protocols/awareness";
import { api } from "@/lib/api";

const b64 = (u8) => { let s = ""; u8.forEach((c) => { s += String.fromCharCode(c); }); return btoa(s); };
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const clients = (doc) => Y.decodeStateVector(Y.encodeStateVector(doc));

export class DocSocket {
  constructor(eid, handlers) {
    this.eid = eid;
    this.h = handlers;
    this.doc = new Y.Doc();
    this.awareness = new Awareness(this.doc);
    this.closed = false;
    this.ready = false;
    this.synced = false;
    this.stale = false;
    this.attempt = 0;
    this.rid = 0;
    this.base = null;
    this.doc.on("update", (u, origin) => {
      if (origin === "remote") return;
      this.send({ type: "update", u: b64(u) });
      if (this.ready && !this.suppress && origin !== "seed") this.h.onLocalChange?.();
    });
    this.awareness.on("update", ({ added, updated, removed }, origin) => {
      if (origin === "remote") return;
      this.send({ type: "awareness", u: b64(encodeAwarenessUpdate(this.awareness, [...added, ...updated, ...removed])) });
    });
    this.wake = () => {
      if (this.closed || this.connecting || (this.ws && this.ws.readyState <= 1)) return;
      clearTimeout(this.retryT);
      this.connect();
    };
    this.onVis = () => { if (document.visibilityState === "visible") this.wake(); };
    window.addEventListener("online", this.wake);
    document.addEventListener("visibilitychange", this.onVis);
    this.connect();
  }

  isOpen() { return this.synced && this.ws?.readyState === 1; }

  send(msg) {
    if (this.isOpen() && !this.stale) this.ws.send(JSON.stringify(msg));
  }

  fields() { return { summary: this.doc.getText("summary").toString(), attributes: this.doc.getMap("attributes").toJSON() }; }

  retry() {
    this.synced = false;
    if (this.closed) return;
    this.h.onStatus?.("offline");
    clearTimeout(this.retryT);
    this.retryT = setTimeout(() => this.connect(), Math.min(30000, 1000 * 2 ** this.attempt++));
  }

  sameLineage(updates) {
    const t = new Y.Doc();
    updates.forEach((u) => Y.applyUpdate(t, unb64(u)));
    const mine = clients(this.doc);
    const same = [...clients(t).keys()].some((k) => mine.has(k));
    t.destroy();
    return same;
  }

  onInitMsg(m) {
    const fresh = !this.ready;
    if (!fresh && m.updates.length && !this.sameLineage(m.updates)) { this.stale = true; this.h.onReset?.(); return; }
    m.updates.forEach((u) => Y.applyUpdate(this.doc, unb64(u), "remote"));
    this.synced = true;
    this.attempt = 0;
    if (fresh) {
      this.base = { md: m.content || "", summary: m.summary || "", attributes: m.attributes || {} };
      this.h.onInit?.(m);
      this.ready = true;
    } else {
      this.send({ type: "update", u: b64(Y.encodeStateAsUpdate(this.doc)) });
      this.h.onStatus?.("live");
      this.h.onReconnect?.(m);
    }
    this.send({ type: "awareness", u: b64(encodeAwarenessUpdate(this.awareness, [this.doc.clientID])) });
  }

  async connect() {
    if (this.closed || this.connecting) return;
    this.connecting = true;
    let token;
    try { token = (await api.get("/auth/ws-token")).data.token; } catch { this.connecting = false; this.retry(); return; }
    this.connecting = false;
    if (this.closed) return;
    const ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/doc/${this.eid}?token=${token}`);
    this.ws = ws;
    ws.onmessage = (e) => {
      if (this.stale) return;
      const m = JSON.parse(e.data);
      if (m.type === "init") this.onInitMsg(m);
      else if (m.type === "update") Y.applyUpdate(this.doc, unb64(m.u), "remote");
      else if (m.type === "awareness") applyAwarenessUpdate(this.awareness, unb64(m.u), "remote");
      else if (m.type === "reset") { this.stale = true; this.h.onReset?.(); }
      else if (m.type === "saved") {
        if (m.rid == null) this.h.onOtherSaved?.();
        else if (m.rid === this.rid) { clearTimeout(this.ackT); this.ackT = null; this.base = this.pendingBase; this.h.onSaved?.(); }
      } else if (m.type === "saved-fields") { clearTimeout(this.fieldsT); this.fieldsT = setTimeout(() => this.h.onFieldsSaved?.(), 1500); }
    };
    ws.onclose = (e) => {
      if (this.closed || ws !== this.ws) return;
      if (e.code === 4403) { this.synced = false; this.h.onStatus?.("denied"); return; }
      this.retry();
    };
  }

  saveAll(markdown) {
    const f = this.fields();
    this.rid += 1;
    this.pendingBase = { md: markdown, ...f };
    this.send({ type: "save", markdown, ...f, rid: this.rid });
    if (this.ackT) return;
    const ws = this.ws;
    this.ackT = setTimeout(() => { this.ackT = null; if (this.ws === ws && !this.closed) { this.ws = null; ws.close(); this.retry(); } }, 8000);
  }

  snapshot(markdown) { return { base: this.base, md: markdown, ...this.fields() }; }

  seedFields(summary, attributes) {
    this.doc.transact(() => {
      const t = this.doc.getText("summary");
      if (!t.length && summary) t.insert(0, summary);
      const m = this.doc.getMap("attributes");
      Object.entries(attributes || {}).forEach(([k, v]) => { if (!m.has(k)) m.set(k, String(v)); });
    }, "seed");
  }

  destroy() {
    this.closed = true;
    clearTimeout(this.retryT);
    clearTimeout(this.ackT);
    window.removeEventListener("online", this.wake);
    document.removeEventListener("visibilitychange", this.onVis);
    removeAwarenessStates(this.awareness, [this.doc.clientID], "local");
    this.ws?.close();
    this.awareness.destroy();
    this.doc.destroy();
  }
}
